import { cn } from '@/lib/cn';

interface HottrMarkProps {
  /** Tailwind text-size class — defaults to text-xl. */
  size?: string;
  /** Add tracking-widest etc. for hero placement. */
  className?: string;
  /**
   * Optional surface suffix, e.g. ".vip" or ".tv".
   *
   * Special case: ".app" renders as plain `HOTTR.` — the primary
   * consumer surface doesn't suffix itself. All other surfaces render
   * `HOTTR.<lowercase>` with the suffix in the SAME display font as the
   * wordmark (not italic-light) so it reads as one logo unit.
   */
  surface?: string;
}

/**
 * The HOTTR. wordmark. The fullstop is the brand's iconic atom — always pink.
 *
 * Display font (Poppins), black weight, uppercase, tight `tracking-display`.
 * Surface suffixes (`vip`, `tv`, etc.) inherit the same weight and font but
 * render lowercase so the lockup reads `HOTTR.vip` as one piece, with the
 * pink fullstop the visual joint.
 *
 * On `.app` the suffix is dropped — we render just `HOTTR.` per canon
 * (the primary consumer surface doesn't suffix itself).
 */
export const HottrMark = ({ size = 'text-xl', className, surface }: HottrMarkProps) => {
  // Strip the leading dot and lowercase. Drop entirely for `.app`.
  const raw = surface?.replace(/^\./, '').toLowerCase();
  const suffix = raw && raw !== 'app' ? raw : '';

  return (
    <span
      className={cn(
        'inline-flex items-baseline font-display font-black uppercase leading-none',
        'tracking-display',
        size,
        className,
      )}
      aria-label={`HOTTR${suffix ? `.${suffix}` : '.'}`}
    >
      <span>HOTTR</span>
      <span className="text-pink">.</span>
      {suffix && (
        <span className="font-display font-black normal-case tracking-display">
          {suffix}
        </span>
      )}
    </span>
  );
};
